/**
 * Presupuestos.
 *
 * La columna que dice a quién hay que llamar hoy son los **días esperando
 * respuesta**: un presupuesto enviado hace diez días y sin abrir no es lo
 * mismo que uno visto ayer.
 */

import { Link, useSearchParams } from 'react-router-dom';
import { DataTable, type Column } from '../table/data-table.js';
import { useListing } from '../table/use-listing.js';
import { money, day } from '../table/format.js';

interface Row {
  id: string;
  number: string;
  status: string;
  issued_on: string;
  valid_until: string | null;
  customer_name: string;
  site_name: string | null;
  total: string;
  currency: string;
  from_anomaly: boolean;
  days_to_expiry: number | null;
  days_waiting: number | null;
}

const STATUS: Record<string, { label: string; tone: string; icon: string }> = {
  BORRADOR: { label: 'Borrador', tone: 'off', icon: '•' },
  ENVIADO: { label: 'Enviado', tone: 'info', icon: '→' },
  VISTO: { label: 'Visto', tone: 'info', icon: '👁' },
  ACEPTADO: { label: 'Aceptado', tone: 'ok', icon: '✓' },
  RECHAZADO: { label: 'Rechazado', tone: 'bad', icon: '✕' },
  VENCIDO: { label: 'Vencido', tone: 'warn', icon: '⚠' },
  UNKNOWN: { label: 'Sin clasificar', tone: 'off', icon: '?' },
};

const COLUMNS: Column<Row>[] = [
  { id: 'number', label: 'Nº', render: (r) => r.number, sortable: true, width: '10rem' },
  { id: 'customerName', label: 'Cliente', render: (r) => r.customer_name },
  { id: 'siteName', label: 'Centro', render: (r) => r.site_name ?? '—' },
  {
    id: 'status',
    label: 'Estado',
    width: '9rem',
    sortable: true,
    render: (r) => {
      const s = STATUS[r.status] ?? STATUS['UNKNOWN']!;
      return <span className={`pill pill--${s.tone}`}>{`${s.icon} ${s.label}`}</span>;
    },
    exportValue: (r) => (STATUS[r.status] ?? STATUS['UNKNOWN']!).label,
  },
  {
    id: 'fromAnomaly',
    label: 'Origen',
    width: '9rem',
    render: (r) =>
      r.from_anomaly ? (
        <span className="pill pill--info">Anomalía</span>
      ) : (
        <span className="pill pill--off">Manual</span>
      ),
    exportValue: (r) => (r.from_anomaly ? 'Anomalía' : 'Manual'),
  },
  { id: 'issuedOn', label: 'Emitido', render: (r) => day(r.issued_on), sortable: true, width: '8rem' },
  {
    id: 'daysWaiting',
    label: 'Esperando',
    numeric: true,
    width: '8rem',
    render: (r) =>
      r.days_waiting === null ? (
        <span className="due--none">—</span>
      ) : r.days_waiting > 7 ? (
        <span className="due due--late">⚠ {r.days_waiting} d</span>
      ) : (
        <span className="due">{r.days_waiting} d</span>
      ),
    exportValue: (r) => r.days_waiting ?? '',
  },
  {
    id: 'total',
    label: 'Total',
    numeric: true,
    width: '10rem',
    render: (r) => money(r.total, r.currency),
    exportValue: (r) => r.total,
  },
];

const COLUMN_IDS = COLUMNS.map((c) => c.id);

const QUICK = [
  { id: 'awaiting', label: 'Esperando respuesta', param: { awaiting: 'true' } },
  { id: 'anomaly', label: 'De anomalías', param: { fromAnomaly: 'true' } },
  { id: 'all', label: 'Todos', param: {} },
] as const;

export function QuotesScreen(): React.ReactNode {
  const [params] = useSearchParams();
  const quick = QUICK.find((q) => q.id === params.get('v')) ?? QUICK[0];

  const listing = useListing<Row>({
    path: '/quotes',
    columnIds: COLUMN_IDS,
    extraParams: quick.param,
    preserve: ['v'],
  });

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Presupuestos</h1>
        <div className="chips" role="group" aria-label="Filtros rápidos">
          {QUICK.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`chip${quick.id === option.id ? ' chip--on' : ''}`}
              aria-pressed={quick.id === option.id}
              onClick={() => listing.setParam('v', option.id === 'awaiting' ? null : option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <Link className="button button--primary button--small" to="/presupuestos/nuevo">
          Nuevo presupuesto
        </Link>
        {listing.total !== null && <span className="screen__count">{listing.total} presupuestos</span>}
      </header>

      {listing.error !== null && (
        <p className="screen__error" role="alert">
          {listing.error}
        </p>
      )}

      <DataTable
        columns={COLUMNS}
        rows={listing.rows}
        idOf={(r) => r.id}
        state={listing.state}
        onStateChange={listing.setState}
        exportName="presupuestos"
        loading={listing.loading}
        hasMore={listing.hasMore}
        onLoadMore={listing.loadMore}
        totalMatching={listing.total}
        emptyMessage="No hay presupuestos que coincidan."
      />
    </section>
  );
}
